// cSpell: ignore describedby
import { NgClass } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, SecurityContext, SimpleChanges, ViewChild, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DomSanitizer } from "@angular/platform-browser";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { ForgeTooltipModule } from "@tylertech/forge-angular";
import { TruncateTextService } from "../../services/truncate.text.service";

/**
 * Just a simple counter so we can generate "unique" ids for each instance
 */
let counter = 0;

@Component({
    selector: "truncate-text",
    imports: [
        ClassDirective,
        ForgeTooltipModule,
        NgClass,
    ],
    templateUrl: "./truncate.text.tmpl.html",
    styleUrls: [
        "./truncate.text.component..scss",
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TruncateTextComponent
    implements AfterViewInit, OnChanges
{
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly $truncateTextService = inject(TruncateTextService);

    @Input() readonly allowHtml: boolean;
    @Input() readonly mainText: string | number | null;
    @Input() readonly multiLine: boolean;

    @ViewChild("contentHost") private readonly contentElement: ElementRef<HTMLSpanElement>;

    readonly bindingId: string;

    readonly showTooltip = signal(false);

    sanitizedContent: string;

    constructor()
    {
        this.bindingId = `truncated-text_${counter++}`;
        this.$truncateTextService.onResize.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                this.showTooltip.set(this.isTruncated());
                this.changeDetectorRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges<TruncateTextComponent>): void
    {
        this.sanitizedContent = this.domSanitizer.sanitize(SecurityContext.HTML, this.mainText);
        if (!changes.mainText.firstChange)
        {

            this.showTooltip.set(this.isTruncated());
            this.changeDetectorRef.markForCheck();
        }
    }

    get content(): string
    {
        return this.sanitizedContent;
    }

    get targetId(): string
    {
        return `#${this.bindingId}`;
    }

    get class(): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "truncated": true,
            "no-wrap": !this.multiLine
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    ngAfterViewInit(): void
    {
        this.showTooltip.set(this.isTruncated());
        this.changeDetectorRef.markForCheck();
    }

    private isTruncated(): boolean
    {
        const contentElement = this.contentElement?.nativeElement;
        if (!contentElement)
        {
            return false;
        }

        if (!this.mainText)
        {
            // TODO - what is adding this attribute? it's not in the template....
            contentElement.removeAttribute("aria-describedby");
            return false;
        }

        const expectedContent = this.allowHtml
            ? this.content
            : this.mainText;
        if (contentElement.innerHTML?.toLowerCase() === expectedContent?.toString()?.toLowerCase())
        {
            const truncate = contentElement.scrollWidth > contentElement.clientWidth;
            if (!truncate)
            {
                contentElement.removeAttribute("aria-describedby");
            }
            return truncate;
        }

        contentElement.removeAttribute("aria-describedby");
        return false;

    }
}
